import i18n from 'i18next';
import { push } from 'redux-first-history';
import { all, call, put, select, takeLeading } from 'redux-saga/effects';

import GLOBAL_ERROR_MESSAGE from '@app/constants/errorMessages';
import documentAuthService from '@app/libs/auth/documentAuthService';
import { isKakaoInAppBrowser } from '@app/libs/browserUserAgent';
import closeKakaoInAppBrowser from '@app/libs/closeKakaoInAppBrowser';
import embeddedService from '@app/libs/embedded/embeddedService';
import redirect from '@app/libs/embedded/redirect';
import workspaceService from '@app/libs/workspaceService';
import {
  CONTRACT_PROGRESS_PARTICIPANT_USER_TYPE_LOAD_FAILURE,
  CONTRACT_PROGRESS_PARTICIPANT_USER_TYPE_LOAD_REQUEST,
  CONTRACT_PROGRESS_REDIRECT_FROM_TOKEN_REQUEST,
  CONTRACT_PROGRESS_REDIRECT_FROM_TOKEN_SUCCESS,
  CONTRACT_PROGRESS_REJECTION_REQUEST,
} from '@app/store/actionTypes';
import {
  contractProgressRedirectFromTokenFailure,
  contractProgressRedirectFromTokenRequest,
  contractProgressRedirectFromTokenSuccess,
  contractProgressRejectionFailure,
  contractProgressRejectionRequest,
  contractProgressRejectionSuccess,
  contractProgressTermsUrlLoadFailure,
  contractProgressTermsUrlLoadRequest,
  contractProgressTermsUrlLoadSuccess,
  loadParticipantUserType,
  loadParticipantUserTypeFailure,
  loadParticipantUserTypeSuccess,
} from '@app/store/contract-progress/actions';
import { getDocumentId } from '@app/store/contract-progress/selectors';
import SigningMethodType from '@app/store/contract-progress/SigningMethodType';
import UserProvider from '@app/store/contract-progress/UserProvider';
import { modalOpen } from '@app/store/modal/actions';
import { getDocumentsParticipantsLinkInfo } from '@app/store/transport-layer/document/documents/participants/link-info';
import { postDocumentsParticipantsRejection } from '@app/store/transport-layer/document/documents/participants/rejection';
import { setDocumentAccessable } from '@app/store/transport-layer/document/documents/signer-access';
import { getDocumentsTerms } from '@app/store/transport-layer/document/documents/terms';
import { getUserProvider } from '@app/store/transport-layer/identityaccess/user/provider';

import Participant from './Participant';
import Signing from './Signing';

/**
 * 계약서에 적용되고 있는 알맞은 버전의 약관 URL을 불러온다.
 */
export function* loadTermsOfURL() {
  const documentId: ReturnType<typeof getDocumentId> = yield select(getDocumentId);
  yield put(contractProgressTermsUrlLoadRequest());

  try {
    const workspaceId = workspaceService.getWorkspaceId();
    const data: Awaited<ReturnType<typeof getDocumentsTerms>> = yield call(getDocumentsTerms, {
      documentId,
      workspaceId,
    } as { workspaceId: string; documentId: string });
    yield put(contractProgressTermsUrlLoadSuccess(data));
  } catch (error) {
    yield put(contractProgressTermsUrlLoadFailure());
  }
}

export const isParticipantLastOrder = (
  participants: Participant[],
  signings: Signing[],
  currentParticipantId: string
) => {
  return participants
    .filter((p) => p.participantId !== currentParticipantId)
    .every((p) =>
      signings
        .filter((signing) => !signing.expiredAt)
        .find((s) => s.participantId === p.participantId)
    );
};

/**
 * 문서 진행을 거절한다.
 * @params {object}
 * @params {string} params.documentId
 * @params {string} params.participantId
 * @params {string} params.message
 * @params {bool} params.isDocumentBlock
 */
export function* rejectDocument({
  payload: { documentId, exitPath, participantId, message, isDocumentBlock },
}: ReturnType<typeof contractProgressRejectionRequest>) {
  try {
    const workspaceId = workspaceService.getWorkspaceId();
    yield call(postDocumentsParticipantsRejection, {
      documentId,
      message,
      participantId,
      workspaceId,
    });
    if (isDocumentBlock) {
      yield call(setDocumentAccessable, { documentId, isAccessable: false, workspaceId });
    }
    yield put(contractProgressRejectionSuccess());

    if (embeddedService.isEmbedded) {
      redirect(embeddedService.redirectUrl, {
        query: {
          documentId,
          eventType: 'document_rejected',
        },
      });
    } else if (isKakaoInAppBrowser() && documentAuthService.isLinkUser()) {
      // 카카오 인앱 브라우저에서 서명요청을 거절, 취소한 경우 유저가 이후에 할 행동이 없으므로 브라우저를 종료합니다.
      if (documentAuthService.hasSavedAccessToken()) {
        documentAuthService.restoreTokens();
      }
      closeKakaoInAppBrowser();
    } else {
      const modalMessage = isDocumentBlock
        ? i18n.t('rejectDocument-rejectMessageWithBlock')
        : i18n.t('rejectDocument-rejectMessageWithoutBlock');
      yield put(
        modalOpen('CONFIRM', {
          title: i18n.t('rejectDocument-rejectTitle'),
          content: modalMessage,
          closeText: i18n.t('Global-confirm'),
        })
      );
      yield put(push(exitPath ?? '/documents'));
    }
  } catch (error) {
    yield put(contractProgressRejectionFailure());
    yield put(
      modalOpen('CONFIRM', {
        title: '거절 실패',
        content: '거절이 실패되었습니다.\n다시 시도해 주세요.',
      })
    );
  }
}

export function* redirectFromToken({
  payload: token,
}: ReturnType<typeof contractProgressRedirectFromTokenRequest>) {
  try {
    const data: {
      documentId: string;
      participantId: string;
      linkKey: string;
      signingMethodType: SigningMethodType;
    } = yield call(getDocumentsParticipantsLinkInfo, token);
    yield put(contractProgressRedirectFromTokenSuccess(data));
  } catch (error) {
    yield put(contractProgressRedirectFromTokenFailure(error));
  }
}

/**
 * 예전에 서명 링크 주소가 /editor/validation/:token 였는데 변경되서
 * 하위 호환을 위해 token 기준으로 링크 정보 조회 후 /participant에
 * di, pi, lk, smt query string 붙여서 리다이렉트 하기 위한 기능입니다.
 * @see: https://modusign.slack.com/archives/C01PP3J0U3H/p1622775710061600
 */
export function* redirectToParticipant({
  payload: { documentId, participantId, linkKey, signingMethodType },
}: ReturnType<typeof contractProgressRedirectFromTokenSuccess>) {
  yield put(
    push(`/participant?di=${documentId}&pi=${participantId}&lk=${linkKey}&smt=${signingMethodType}`)
  );
}

export function* loadProvider({ payload: email }: ReturnType<typeof loadParticipantUserType>) {
  try {
    const provider: UserProvider = yield call(getUserProvider, email);
    yield put(loadParticipantUserTypeSuccess(provider));
  } catch (error) {
    yield put(loadParticipantUserTypeFailure(error));
  }
}

export function* notifyLoadParticipantUserTypeFailure({
  payload,
}: ReturnType<typeof loadParticipantUserTypeFailure>) {
  yield put(
    modalOpen('CONFIRM', {
      title: '유저 타입 불러오기 실패',
      content: GLOBAL_ERROR_MESSAGE[payload.message] || payload.message,
    })
  );
}

export default function* editorSagas() {
  yield all([
    takeLeading(CONTRACT_PROGRESS_REJECTION_REQUEST, rejectDocument),
    takeLeading(CONTRACT_PROGRESS_REDIRECT_FROM_TOKEN_REQUEST, redirectFromToken),
    takeLeading(CONTRACT_PROGRESS_REDIRECT_FROM_TOKEN_SUCCESS, redirectToParticipant),
    takeLeading(CONTRACT_PROGRESS_PARTICIPANT_USER_TYPE_LOAD_REQUEST, loadProvider),
    takeLeading(
      CONTRACT_PROGRESS_PARTICIPANT_USER_TYPE_LOAD_FAILURE,
      notifyLoadParticipantUserTypeFailure
    ),
  ]);
}
